import { usePageTitle } from '../hooks/usePageTitle';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../AuthContext';
import { useTheme } from '../contexts/ThemeContext';

interface AdayOzet {
  id: string; ad: string; soyad: string | null; email: string | null; tel: string | null;
  cinsiyet: string | null; durum: string | null; kaynak: string | null;
  sube_adi: string | null; sube_adi_eski: string | null; gorusme_sayisi: string;
  ogrenci_id: string | null; ogrenci_ad: string | null; ogrenci_soyad: string | null;
}
interface Sube { id: string; ad: string; }
interface Gorusme { id: string; icerik: string; tarih: string | null; saat: string | null; }
interface AdayDetay extends AdayOzet { notlar: string | null; ilk_kayit_tarihi: string | null; gorusmeler: Gorusme[]; }
interface ErisimKaydi { id: string; email: string; olusturuldu: string; }

const DURUM_LABEL: Record<string, string> = { yeni: 'Yeni', iletisimde: 'İletişimde', ilgileniyor: 'İlgileniyor', deneme: 'Deneme', kayit_oldu: 'Kayıt Oldu', vazgecti: 'Vazgeçti' };
const KAYNAK_LABEL: Record<string, string> = { google: 'Google', facebook: 'Facebook', arkadas_tavsiyesi: 'Arkadaş Tavsiyesi', gazete_reklami: 'Gazete Reklamı', gazete_haberi: 'Gazete Haberi', televizyon: 'Televizyon', brosur: 'Broşür', diger: 'Diğer' };

function formatTarih(t: string | null) {
  if (!t) return '—';
  return new Date(t).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function Arsiv() {
  usePageTitle('Arşiv');
  const navigate = useNavigate();
  const { kullanici } = useAuth();
  const { isDark } = useTheme();
  const isSuperAdmin = kullanici?.rol === 'super_admin';

  const card = isDark ? '#1a1a1a' : 'white';
  const border = isDark ? '#2a2a2a' : '#eee';
  const borderSub = isDark ? '#202020' : '#f0f0f0';
  const textPrimary = isDark ? '#e8e8e8' : '#1a1a1a';
  const textSec = isDark ? '#999' : '#888';
  const textMuted = isDark ? '#666' : '#aaa';
  const inp: React.CSSProperties = { padding: '8px 12px', border: `1px solid ${border}`, borderRadius: 8, fontSize: 14, outline: 'none', background: isDark ? '#111' : 'white', color: textPrimary, boxSizing: 'border-box' };

  const [arama, setArama] = useState('');
  const [aramaDebounced, setAramaDebounced] = useState('');
  const [sayfa, setSayfa] = useState(1);
  const [subeFiltre, setSubeFiltre] = useState('');
  const [zatenOgrenciFiltre, setZatenOgrenciFiltre] = useState('');
  const [subeler, setSubeler] = useState<Sube[]>([]);
  const [zatenOgrenciSayisi, setZatenOgrenciSayisi] = useState<number | null>(null);
  const [kayitlar, setKayitlar] = useState<AdayOzet[]>([]);
  const [toplam, setToplam] = useState(0);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [secilenId, setSecilenId] = useState<string | null>(null);
  const [detay, setDetay] = useState<AdayDetay | null>(null);
  const [detayYukleniyor, setDetayYukleniyor] = useState(false);

  const [erisimAcik, setErisimAcik] = useState(false);
  const [erisimListesi, setErisimListesi] = useState<ErisimKaydi[]>([]);
  const [yeniEmail, setYeniEmail] = useState('');
  const [erisimHata, setErisimHata] = useState('');
  const [exportYukleniyor, setExportYukleniyor] = useState(false);

  const limit = 50;

  useEffect(() => {
    const t = setTimeout(() => { setAramaDebounced(arama); setSayfa(1); }, 400);
    return () => clearTimeout(t);
  }, [arama]);

  useEffect(() => {
    api.get('/api/subeler').then(r => setSubeler(r.data)).catch(() => {});
    api.get('/api/arsiv/istatistik/zaten-ogrenci').then(r => setZatenOgrenciSayisi(r.data.sayi)).catch(() => {});
  }, []);

  useEffect(() => { setSayfa(1); }, [subeFiltre, zatenOgrenciFiltre]);

  useEffect(() => {
    setYukleniyor(true);
    api.get('/api/arsiv', {
      params: {
        arama: aramaDebounced || undefined,
        sube_id: subeFiltre || undefined,
        zaten_ogrenci: zatenOgrenciFiltre || undefined,
        sayfa, limit,
      },
    })
      .then(r => { setKayitlar(r.data.kayitlar); setToplam(r.data.toplam); })
      .finally(() => setYukleniyor(false));
  }, [aramaDebounced, subeFiltre, zatenOgrenciFiltre, sayfa]);

  function detayAc(id: string) {
    setSecilenId(id);
    setDetayYukleniyor(true);
    api.get(`/api/arsiv/${id}`).then(r => setDetay(r.data)).finally(() => setDetayYukleniyor(false));
  }

  function erisimYonetimiAc() {
    setErisimAcik(true);
    api.get('/api/arsiv/erisim/liste').then(r => setErisimListesi(r.data));
  }

  async function erisimEkle() {
    if (!yeniEmail.trim()) return;
    setErisimHata('');
    try {
      const r = await api.post('/api/arsiv/erisim', { email: yeniEmail.trim() });
      setErisimListesi(prev => [...prev, r.data]);
      setYeniEmail('');
    } catch (e: any) { setErisimHata(e.response?.data?.hata || 'Eklenemedi'); }
  }

  async function erisimSil(id: string) {
    await api.delete(`/api/arsiv/erisim/${id}`);
    setErisimListesi(prev => prev.filter(x => x.id !== id));
  }

  async function disaAktar() {
    setExportYukleniyor(true);
    try {
      const r = await api.get('/api/arsiv/export', {
        params: {
          arama: aramaDebounced || undefined,
          sube_id: subeFiltre || undefined,
          zaten_ogrenci: zatenOgrenciFiltre || undefined,
        },
        responseType: 'blob',
        timeout: 30000,
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `arsiv-export-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      let mesaj = 'Export başarısız oldu';
      const blob = e?.response?.data;
      if (blob instanceof Blob) {
        try {
          const metin = await blob.text();
          mesaj = JSON.parse(metin)?.hata || mesaj;
        } catch { /* blob JSON değilse mesaj varsayılan kalır */ }
      } else if (e?.message) {
        mesaj = e.message;
      }
      alert(mesaj);
    } finally {
      setExportYukleniyor(false);
    }
  }

  const td: React.CSSProperties = { padding: '11px 14px', fontSize: 13, verticalAlign: 'middle', color: textPrimary };
  const toplamSayfa = Math.max(Math.ceil(toplam / limit), 1);

  return (
    <div style={{ padding: 32, maxWidth: 1000 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: textPrimary }}>Arşiv</h2>
          <p style={{ margin: '4px 0 0', color: textSec, fontSize: 13 }}>
            Eski sistemden aktarılan CRM adayları ({toplam.toLocaleString('tr-TR')} kayıt) — canlı CRM'e karışmaz, salt görüntüleme
            {zatenOgrenciSayisi !== null && zatenOgrenciSayisi > 0 && (
              <> · <span style={{ color: '#16a34a', fontWeight: 600 }}>{zatenOgrenciSayisi} kişi telefonuyla zaten öğrenci</span></>
            )}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button onClick={disaAktar} disabled={exportYukleniyor} style={{ background: card, color: '#16a34a', border: '1px solid #16a34a', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: exportYukleniyor ? 'default' : 'pointer', opacity: exportYukleniyor ? 0.6 : 1 }}>
            {exportYukleniyor ? 'Hazırlanıyor…' : '⬇️ Brevo için Dışa Aktar (CSV)'}
          </button>
          {isSuperAdmin && (
            <button onClick={erisimYonetimiAc} style={{ background: card, color: '#3C3489', border: '1px solid #3C3489', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              🔒 Erişim Yönetimi
            </button>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        <input
          style={{ ...inp, width: 300 }}
          value={arama} onChange={e => setArama(e.target.value)}
          placeholder="Ad, soyad, telefon veya e-posta ara..."
        />
        <select style={inp} value={subeFiltre} onChange={e => setSubeFiltre(e.target.value)}>
          <option value="">Tüm Şubeler</option>
          {subeler.map(s => <option key={s.id} value={s.id}>{s.ad}</option>)}
        </select>
        <select style={inp} value={zatenOgrenciFiltre} onChange={e => setZatenOgrenciFiltre(e.target.value)}>
          <option value="">Öğrenci Durumu — Tümü</option>
          <option value="true">Zaten Öğrenci Olanlar</option>
          <option value="false">Henüz Öğrenci Olmayanlar</option>
        </select>
      </div>

      {yukleniyor ? (
        <p style={{ color: textMuted }}>Yükleniyor…</p>
      ) : (
        <>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: card, borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
            <thead>
              <tr style={{ background: isDark ? '#1e1e1e' : '#f5f5f5', fontSize: 12, color: textSec, textTransform: 'uppercase' }}>
                {['Ad Soyad', 'Telefon', 'Durum', 'Kaynak', 'Şube', 'Görüşme', 'Öğrenci mi?'].map(c => (
                  <th key={c} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600 }}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {kayitlar.map((k, i) => (
                <tr key={k.id} onClick={() => detayAc(k.id)}
                  style={{ borderTop: i ? `1px solid ${borderSub}` : undefined, cursor: 'pointer' }}
                  onMouseEnter={e => (e.currentTarget.style.background = isDark ? '#1e1e1e' : '#fafafa')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                  <td style={td}><span style={{ fontWeight: 600 }}>{k.ad} {k.soyad || ''}</span></td>
                  <td style={{ ...td, color: textSec }}>{k.tel || '—'}</td>
                  <td style={td}>{k.durum ? DURUM_LABEL[k.durum] || k.durum : '—'}</td>
                  <td style={{ ...td, color: textSec, fontSize: 12 }}>{k.kaynak ? KAYNAK_LABEL[k.kaynak] || k.kaynak : '—'}</td>
                  <td style={{ ...td, color: textSec, fontSize: 12 }}>{k.sube_adi || k.sube_adi_eski || '—'}</td>
                  <td style={{ ...td, color: textMuted, fontSize: 12 }}>{k.gorusme_sayisi}</td>
                  <td style={td}>
                    {k.ogrenci_id ? (
                      <span
                        onClick={ev => { ev.stopPropagation(); navigate(`/ogrenciler/${k.ogrenci_id}`); }}
                        style={{ color: '#16a34a', fontWeight: 600, fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}
                        title={`${k.ogrenci_ad} ${k.ogrenci_soyad} profiline git`}
                      >
                        ✓ {k.ogrenci_ad}
                      </span>
                    ) : (
                      <span style={{ color: textMuted, fontSize: 12 }}>—</span>
                    )}
                  </td>
                </tr>
              ))}
              {!kayitlar.length && (
                <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: textMuted }}>Sonuç bulunamadı</td></tr>
              )}
            </tbody>
          </table>

          {toplamSayfa > 1 && (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', alignItems: 'center', marginTop: 16 }}>
              <button disabled={sayfa <= 1} onClick={() => setSayfa(s => s - 1)}
                style={{ padding: '6px 12px', borderRadius: 6, border: `1px solid ${border}`, background: card, color: textPrimary, cursor: sayfa <= 1 ? 'default' : 'pointer', opacity: sayfa <= 1 ? 0.4 : 1 }}>← Önceki</button>
              <span style={{ fontSize: 13, color: textSec }}>{sayfa} / {toplamSayfa}</span>
              <button disabled={sayfa >= toplamSayfa} onClick={() => setSayfa(s => s + 1)}
                style={{ padding: '6px 12px', borderRadius: 6, border: `1px solid ${border}`, background: card, color: textPrimary, cursor: sayfa >= toplamSayfa ? 'default' : 'pointer', opacity: sayfa >= toplamSayfa ? 0.4 : 1 }}>Sonraki →</button>
            </div>
          )}
        </>
      )}

      {/* Detay modal */}
      {secilenId && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => { setSecilenId(null); setDetay(null); }}>
          <div style={{ background: card, borderRadius: 14, padding: 26, width: 500, maxHeight: '80vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}>
            {detayYukleniyor || !detay ? (
              <p style={{ color: textMuted }}>Yükleniyor…</p>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <h3 style={{ margin: 0, fontSize: 17, color: textPrimary }}>{detay.ad} {detay.soyad}</h3>
                  <button onClick={() => { setSecilenId(null); setDetay(null); }} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: textMuted }}>×</button>
                </div>
                <div style={{ fontSize: 13, color: textSec, marginBottom: 16 }}>
                  {detay.tel && <div>📞 {detay.tel}</div>}
                  {detay.email && <div>✉️ {detay.email}</div>}
                  <div>Durum: {detay.durum ? DURUM_LABEL[detay.durum] || detay.durum : '—'} · Kaynak: {detay.kaynak ? KAYNAK_LABEL[detay.kaynak] || detay.kaynak : '—'}</div>
                  <div>Şube: {detay.sube_adi || detay.sube_adi_eski || '—'} · İlk kayıt: {formatTarih(detay.ilk_kayit_tarihi)}</div>
                  {detay.notlar && <div style={{ marginTop: 6 }}>📝 {detay.notlar}</div>}
                  {detay.ogrenci_id && (
                    <div
                      onClick={() => navigate(`/ogrenciler/${detay.ogrenci_id}`)}
                      style={{ marginTop: 8, color: '#16a34a', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}
                    >
                      ✓ Bu telefon numarasıyla zaten öğrenci: {detay.ogrenci_ad} {detay.ogrenci_soyad} →
                    </div>
                  )}
                </div>

                <div style={{ fontSize: 14, fontWeight: 700, color: textPrimary, marginBottom: 8 }}>Görüşme Geçmişi ({detay.gorusmeler.length})</div>
                {detay.gorusmeler.length === 0 ? (
                  <p style={{ color: textMuted, fontSize: 13 }}>Görüşme kaydı yok</p>
                ) : (
                  detay.gorusmeler.map(g => (
                    <div key={g.id} style={{ padding: '9px 0', borderBottom: `1px solid ${borderSub}` }}>
                      <div style={{ fontSize: 11, color: textMuted, marginBottom: 3 }}>{formatTarih(g.tarih)}{g.saat ? ` · ${g.saat}` : ''}</div>
                      <div style={{ fontSize: 13, color: textPrimary, whiteSpace: 'pre-wrap' }}>{g.icerik}</div>
                    </div>
                  ))
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Erişim yönetimi modal */}
      {erisimAcik && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setErisimAcik(false)}>
          <div style={{ background: card, borderRadius: 14, padding: 26, width: 420, maxHeight: '80vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <h3 style={{ margin: 0, fontSize: 17, color: textPrimary }}>Arşiv Erişim Yönetimi</h3>
              <button onClick={() => setErisimAcik(false)} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: textMuted }}>×</button>
            </div>
            <p style={{ fontSize: 12, color: textSec, marginBottom: 16 }}>Süper admin her zaman erişir. Buraya eklenen email'ler de (rolü ne olursa olsun) arşive erişebilir.</p>

            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <input style={{ ...inp, flex: 1 }} value={yeniEmail} onChange={e => setYeniEmail(e.target.value)} placeholder="email@ornek.com" onKeyDown={e => e.key === 'Enter' && erisimEkle()} />
              <button onClick={erisimEkle} style={{ background: '#3C3489', color: 'white', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Ekle</button>
            </div>
            {erisimHata && <div style={{ color: '#EF4444', fontSize: 12, marginBottom: 12 }}>{erisimHata}</div>}

            {erisimListesi.map(e => (
              <div key={e.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${borderSub}` }}>
                <span style={{ fontSize: 13, color: textPrimary }}>{e.email}</span>
                <button onClick={() => erisimSil(e.id)} style={{ background: 'transparent', color: '#dc2626', border: '1px solid #dc2626', borderRadius: 6, padding: '3px 9px', fontSize: 11, cursor: 'pointer' }}>Kaldır</button>
              </div>
            ))}
            {!erisimListesi.length && <p style={{ color: textMuted, fontSize: 13 }}>Henüz kimse eklenmedi</p>}
          </div>
        </div>
      )}
    </div>
  );
}
